import HttpStatus from 'http-status-codes'
import i18n from '@/utils/i18n'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import { save } from '@/utils/xfs'

/**
 * Whether `getRegionEnvsByCode` should retry after this HTTP/client failure (timeouts, 429, 5xx, or no response).
 * @param {object} err Axios-style error (`response`, `code`, etc.).
 * @returns {boolean}
 * @date 2026-04-10
 * @author Alvis Yip
 */
function shouldRetryTransientRequestError (err) {
  const status = err && err.response && err.response.status

  if (!err.response) return true
  if (err.code === 'ECONNABORTED') return true

  return status === HttpStatus.REQUEST_TIMEOUT ||
    status === HttpStatus.TOO_MANY_REQUESTS ||
    status >= HttpStatus.INTERNAL_SERVER_ERROR
}

/**
 * Promise-based delay for retry backoff.
 * @param {number} ms Milliseconds to wait.
 * @returns {Promise<void>}
 * @date 2026-04-10
 * @author Alvis Yip
 */
function wait (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export async function getThreshold (code) {
  const notificationPrefix = `[${i18n.t('settings.threshold.getThresholdFailed')}] `
  try {
    const response = await HTTP.get(`/settings/${code}`)
    return response.data.data.value
  } catch (err) {
    notification.handleResponseError(err, notificationPrefix)
    return 0
  }
}

export async function getAllSettings () {
  try {
    const response = await HTTP.get('/settings/')
    return response.data.data
  } catch (err) {
    console.error(err)
    return []
  }
}

export async function getEnvParameter (code) {
  try {
    const response = await HTTP.get(`/settings/${code}?isEnv=true`)
    return response.data.data
  } catch (err) {
    console.error(err)
    return 0
  }
}

export async function getTimeoutSettings () {
  try {
    const response = await HTTP.get('/settings/client/security.timeout')
    return response.data.data
  } catch (err) {
    console.error(err)
    return {}
  }
}

export async function getECircularEmailSettings () {
  try {
    const response = await HTTP.get('/settings/client/user.maxNumOfECircularEmails')
    return response.data.data
  } catch (err) {
    console.error(err)
    return {}
  }
}

export async function getRegionEnvsByCode (code) {
  const maxAttempts = 3

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await HTTP.get(`/settings/regionenvs/${code}`)
      return response.data.data
    } catch (err) {
      console.error(err)

      const isLastAttempt = attempt === maxAttempts
      if (isLastAttempt || !shouldRetryTransientRequestError(err)) {
        return {}
      }

      await wait(attempt * 150)
    }
  }

  return {}
}

export async function getRegionEnvsList (codes) {
  try {
    const response = await HTTP.get('/settings/list/regionenvs', { params: { codes } })
    return response.data.data
  } catch (err) {
    console.error(err)
    return {}
  }
}

export async function updateThreshold (codeValuePairs, callback, errCallback) {
  await HTTP.put('/settings/', codeValuePairs)
    .then((resp) => {
      if (resp.status === HttpStatus.OK) {
        callback()
      }
      notification.handleResponseAlert(resp)
    })
    .catch((err) => {
      notification.handleResponseError(err)
      errCallback()
    })
}

export async function getGuidelines (status, type, isSilentGet) {
  try {
    const get = isSilentGet ? HTTP.silentGet : HTTP.get
    const response = await get(`/guidelines/${status}?type=${type}`)
    return response.data.data
  } catch (err) {
    console.error(err)
    return []
  }
}

export async function getHardCodeBuildingFloorMap () {
  try {
    const response = await HTTP.get(`/settings/hardCodeBuildingFloorMap`)
    return JSON.parse(response.data.data.value)
  } catch (err) {
    console.error(err)
    return {}
  }
}

export async function updateGuidelines (request) {
  try {
    const response = await HTTP.put('/guidelines', request)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function publishGuidelines (request) {
  try {
    const response = await HTTP.patch(`/guidelines?type=${request.type}`)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
  }
}

export async function getTemplates (type) {
  try {
    const response = await HTTP.get('/templates', { params: { type } })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function updateTemplate (request, type) {
  try {
    const response = await HTTP.put('/templates/by-type', { data: request, type })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
  }
}

export async function getRegionalEnvs () {
  try {
    const response = await HTTP.get(`settings/all/regionenvs`)
    return response.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getTemplatesNEntitlements () {
  try {
    const response = await HTTP.get(`settings/all/regionenvs/templates_entitlement`)
    return response.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateReginalEnvs (request) {
  await HTTP.put('/settings/regionenvs/update', request)
}

export async function getRegionImg () {
  try {
    const response = await HTTP.get(`region-images/list`)
    return response.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateReginImg (request) {
  await HTTP.put('/region-images/update', request)
}

export async function exportContent (request) {
  try {
    const response = await HTTP.post(`filestream/json/export`, request, { responseType: 'arraybuffer' })
    save(response)
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export const getMaxNumOfVendorSubAccount = async () => {
  const result = await getEnvParameter('user.maxNumOfVendorSubAccount')
  return result || 6
}

export async function getAdUserDomains () {
  try {
    const response = await HTTP.get('settings/ad_user/domains')
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}
